/**
 * Obraven motion (Theme settings › Animations), configured through data attributes on <html>:
 * - data-ob-reveal: none | fade-up | fade | zoom | slide — sections, section headings and cards reveal on scroll
 * - data-ob-stagger: cards/tiles in the same row reveal one after another
 * - data-ob-hero-intro: hero text rises in and the photo settles when the page loads
 * - data-ob-parallax: banner photos drift slightly while scrolling (pure CSS, assets/ob-animations.css)
 * Hidden "before" states only exist while <html> has .ob-motion-ready, which this script adds, so content
 * is never hidden without JavaScript, under prefers-reduced-motion or in the theme editor.
 */
(() => {
  const root = document.documentElement;
  const config = root.dataset;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const designMode = window.Shopify && window.Shopify.designMode;
  if (reducedMotion) return;

  const revealStyle = config.obReveal || 'none';
  const stagger = config.obStagger === 'true';
  // Set by snippets/page-transitions.liquid when this page arrives through a page transition: what is already on
  // screen comes in with the page, so it skips its own entrance animation (which would play on top of the transition).
  const arriving = root.classList.contains('ob-vt-arrival');
  const onScreen = (element) => {
    const rect = element.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight;
  };

  // Repeating items: each reveals on its own, staggered within its row.
  const ITEM_SELECTOR = [
    'product-card',
    '.ob-carousel__item',
    '.ob-collection__item',
    '.ob-collist__item',
    '.ob-coltiles__item',
    '.ob-fctabs__item',
    '.ob-related__item',
    '.ob-split__item',
    '.ob-why__card',
    '.ob-values__card',
    '.ob-promise__card',
    '.ob-insta__item',
    '.ob-image-strip__item',
    '.ob-contact-cards__item',
    '.ob-faq__item',
    '.ob-blog__item',
    '.ob-featured-blog__item',
    '.ob-wishlist__item',
    '.ob-custom__content > *',
  ].join(',');
  const HEADER_SELECTOR = '.ob-section-header';
  // Sections that should be on screen immediately (they own their own entrance or motion, or are the page's
  // main task). The marquee band peeks in under the hero, so it must never wait for a scroll.
  const SKIP_SECTION = '.ob-hero, .ob-marquee, product-info, .ob-product, #CartDrawer, .ob-404';

  let observer = null;

  function revealNow(element) {
    element.classList.add('is-revealed');
    observer?.unobserve(element);
  }

  function prepare(element, index = 0, skipOnScreen = false) {
    if (element.classList.contains('ob-reveal')) return;
    if (skipOnScreen && onScreen(element)) return;
    element.classList.add('ob-reveal');
    element.style.setProperty('--ob-reveal-index', String(stagger ? Math.min(index, 6) : 0));
    observer.observe(element);
  }

  function prepareItems(items, skipOnScreen = false) {
    const indexByParent = new Map();
    items.forEach((item) => {
      const parent = item.parentElement;
      const index = indexByParent.get(parent) || 0;
      indexByParent.set(parent, index + 1);
      prepare(item, index, skipOnScreen);
    });
  }

  function topLevelItems(scope) {
    return [...scope.querySelectorAll(ITEM_SELECTOR)].filter(
      (item) => !item.parentElement.closest(ITEM_SELECTOR) && !item.closest('#CartDrawer, .ob-search-modal')
    );
  }

  function prepareSection(section, skipOnScreen = false) {
    if (section.querySelector(SKIP_SECTION)) return;
    const headers = [...section.querySelectorAll(HEADER_SELECTOR)];
    const items = topLevelItems(section);
    headers.forEach((header) => prepare(header, 0, skipOnScreen));
    prepareItems(items, skipOnScreen);
    if (headers.length === 0 && items.length === 0) {
      // Sections start with <link>/<style>/<script> tags; reveal the first visible element instead.
      const content = [...section.children].find(
        (child) => !['LINK', 'STYLE', 'SCRIPT', 'NOSCRIPT', 'TEMPLATE'].includes(child.tagName)
      );
      if (content) prepare(content, 0, skipOnScreen);
    }
  }

  function initReveal() {
    if (revealStyle === 'none' || designMode || !('IntersectionObserver' in window)) return;
    root.classList.add('ob-motion-ready');
    observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          // Reveal on entering the viewport, and anything already scrolled past (back navigation, anchors).
          if (entry.isIntersecting || entry.boundingClientRect.bottom < 0) revealNow(entry.target);
        }),
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );
    document.querySelectorAll('main .shopify-section').forEach((section) => prepareSection(section, arriving));

    // Whatever is already on screen when the page opens plays its entrance straight away, including a
    // section peeking in at the bottom edge, which the observer's -8% margin would otherwise leave blank.
    // Two frames so the hidden state paints first and the entrance still animates.
    requestAnimationFrame(() =>
      requestAnimationFrame(() =>
        document.querySelectorAll('main .ob-reveal:not(.is-revealed)').forEach((element) => {
          if (onScreen(element)) revealNow(element);
        })
      )
    );

    // Cards added later (Load more, filters, tabs, recommendations) reveal too.
    new MutationObserver((mutations) => {
      const added = mutations.flatMap((m) => [...m.addedNodes]).filter((n) => n.nodeType === 1);
      if (!added.length) return;
      const fresh = added.flatMap((node) => [
        ...(node.matches(ITEM_SELECTOR) ? [node] : []),
        ...topLevelItems(node),
      ]);
      prepareItems(fresh.filter((el) => el.closest('main') && !el.classList.contains('ob-reveal')));
    }).observe(document.querySelector('main') || document.body, { childList: true, subtree: true });
  }

  function initHeroIntro() {
    if (config.obHeroIntro !== 'true' || designMode || arriving) return;
    const slide = document.querySelector('.ob-hero__slide.is-active, .ob-hero__slide');
    const content = slide?.querySelector('.ob-hero__content');
    if (!content) return;
    [...content.children].forEach((child, index) => child.style.setProperty('--ob-intro-index', String(index)));
    slide.closest('.ob-hero')?.classList.add('ob-hero--intro');
  }

  const start = () => {
    initHeroIntro();
    initReveal();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
